import { FC, useMemo } from "react";
import { CandidateListHeader } from "./CandidateListHeader";
import { CandidatesTable } from "./CandidateListTable";
import { LoadingTable } from "./LoadingTable";
import { MoreLess } from "./MoreLess";
import { Candidate, ElectionType, ListName } from "./types";
import { useCandidateList } from "./useCandidateList";

interface Props {
  electionTypes: ElectionType[];
  listNames: ListName[];
  selectedElectionType: ElectionType;
  selectedListName: ListName;
  showAllCandidates: boolean;
  copy: Record<string, string | undefined>;
  initialCandidatesList: Candidate[];
  showElectedCandidatesOnly: boolean;
}

export const CandidateList: FC<Props> = ({
  copy,
  electionTypes,
  listNames,
  selectedElectionType,
  selectedListName,
  showAllCandidates,
  initialCandidatesList,
  showElectedCandidatesOnly,
}) => {
  const {
    selectedElection,
    setSelectedElection,
    selectedList,
    setSelectedList,
    candidates,
    substitutes,
    isLoading,
    filteredListNames,
    isOpen,
    setIsOpen,
  } = useCandidateList({
    selectedElectionType,
    selectedListName,
    listNames,
    initialCandidatesList,
    showAllCandidates,
    showElectedCandidatesOnly,
  });

  const candidatesTable = useMemo(() => {
    if (isLoading) return <LoadingTable />;

    if (showElectedCandidatesOnly && selectedElection) {
      return (
        <CandidatesTable
          copy={copy}
          candidates={[...candidates, ...substitutes]}
          showElectedCandidatesOnly
        />
      );
    }

    if (selectedElection && selectedList) {
      return (
        <>
          <CandidatesTable copy={copy} candidates={candidates} />
          {candidates.length > 4 && (
            <MoreLess copy={copy} isOpen={isOpen} setIsOpen={setIsOpen} />
          )}
          {!!substitutes.length && (
            <>
              <h2>{copy.candidateListSubstitutes}</h2>
              <CandidatesTable
                copy={copy}
                candidates={substitutes}
                isSubstitutes
              />
            </>
          )}
        </>
      );
    }

    return (
      <p className="candidate-list__empty">{showElectedCandidatesOnly
        ? copy.candidateListHelpTextPostElection
        : copy.candidateListHelpText
      }</p>
    );
  }, [
    candidates,
    copy,
    isLoading,
    isOpen,
    selectedElection,
    selectedList,
    setIsOpen,
    showElectedCandidatesOnly,
    substitutes,
  ]);

  return (
    <>
      <CandidateListHeader
        copy={copy}
        electionTypes={electionTypes}
        listNames={filteredListNames}
        selectedElection={selectedElection}
        selectedListName={selectedList}
        setSelectedElectionType={setSelectedElection}
        setSelectedListName={setSelectedList}
        showElectedCandidatesOnly={showElectedCandidatesOnly}
      />

      {candidatesTable}
    </>
  );
};
